import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { isFeatureEnabled } from '../../../services/featureEnabledService';

/**
 * Feature flag for the refactored (V2) published shift plan page.
 * Gated via the RnRRCCFacadeService IsFeatureEnabled / AppConfig mechanism
 * (see UTR-REFLEX-1164). Registered as `RCC_PUBLISHED_SHIFT_PLAN_V2` in the
 * facade's FeatureName enum + FeatureGating AppConfig allowlist.
 */
const PUBLISHED_SHIFT_PLAN_V2_FEATURE_NAME = 'RCC_PUBLISHED_SHIFT_PLAN_V2';

// ─── State Interface ────────────────────────────────────────────────────────────

/**
 * One-shot pointer to the plan an operator just published, set by the publish flow
 * (UTR-REFLEX-1163) right before it returns to the home summary. The home page uses
 * it to load the right shift and open the V2 page on that Operator Override plan
 * (instead of resetting to today's default and showing a blank Override view).
 * Cleared once the page has consumed it.
 */
export interface PostPublishView {
  planId: string;
  /** OFD date (YYYY-MM-DD). */
  date: string;
  /** Shift option value, e.g. 'day' | 'night'. */
  shift: string;
  /** Plan-type tab value, e.g. 'sos' | 'pdp'. */
  tab: string;
}

export interface PublishedShiftPlanFeatureState {
  /**
   * Whether the refactored published shift plan page is enabled
   * (from the isFeatureEnabled API). `null` = not yet resolved — callers
   * should wait before choosing which UI to render.
   */
  isPublishedShiftPlanV2Enabled: boolean | null;
  /** The just-published plan to navigate to on the home summary (or null). */
  postPublishView: PostPublishView | null;
}

// ─── Initial State ──────────────────────────────────────────────────────────────

const initialState: PublishedShiftPlanFeatureState = {
  isPublishedShiftPlanV2Enabled: null,
  postPublishView: null,
};

// ─── Async Thunks ───────────────────────────────────────────────────────────────

export const checkPublishedShiftPlanV2Enabled = createAsyncThunk<
  boolean,
  string,
  { rejectValue: string }
>(
  'publishedShiftPlan/checkFeatureEnabled',
  async (stationCode, { rejectWithValue }) => {
    try {
      // Pass the station so the facade's site-level gating (FeatureGating.<feature>.AllowedSites)
      // can enable the page for an entire site, not just allowlisted users.
      const enabled = await isFeatureEnabled(PUBLISHED_SHIFT_PLAN_V2_FEATURE_NAME, stationCode);
      return enabled;
    } catch (error: unknown) {
      return rejectWithValue('Failed to check feature flag');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────────────────────────

const publishedShiftPlanFeatureSlice = createSlice({
  name: 'publishedShiftPlanFeature',
  initialState,
  reducers: {
    /**
     * Directly set the flag without hitting the facade. Used in local dev where
     * getUserName() (and therefore isFeatureEnabled) can't resolve without a Boson
     * parent frame — mirrors AgentChatPanelGated's local-dev short-circuit.
     */
    setPublishedShiftPlanV2Enabled(state, action: PayloadAction<boolean>) {
      state.isPublishedShiftPlanV2Enabled = action.payload;
    },
    /** Record the just-published plan to navigate to on the home summary. */
    setPostPublishView(state, action: PayloadAction<PostPublishView>) {
      state.postPublishView = action.payload;
    },
    /** Clear the one-shot pointer once the home page has consumed it. */
    clearPostPublishView(state) {
      state.postPublishView = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkPublishedShiftPlanV2Enabled.fulfilled, (state, action) => {
        state.isPublishedShiftPlanV2Enabled = action.payload;
      })
      .addCase(checkPublishedShiftPlanV2Enabled.rejected, (state) => {
        // Fail closed — fall back to the existing (V1) page on error.
        state.isPublishedShiftPlanV2Enabled = false;
      });
  },
});

export const { setPublishedShiftPlanV2Enabled, setPostPublishView, clearPostPublishView } = publishedShiftPlanFeatureSlice.actions;

export default publishedShiftPlanFeatureSlice.reducer;
